import { cx } from 'linaria';
import * as React from 'react';
import { states, Typography } from '../';
import { IconCheck, IconSubtract } from '../foundation/icons';
import * as styles from './checkbox.styles';

export interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
    /**
     *  Label for checkbox input
     */
    label: string | React.ReactNode;
    /**
     *  Group multiple checkboxes by name
     */
    name?: string;
    /**
     *  Value for the checkbox
     */
    value?: string;
    /**
     *  Make field a required one
     */
    required?: boolean;
    /**
     *  Display checkbox in readonly state
     */
    readOnly?: boolean;
    /**
     *  Display checkbox in disabled state
     */
    disabled?: boolean;
    /**
     *  Controlled - Determines if checkbox is checked or not
     */
    checked?: boolean;
    /**
     *  Uncontrolled - Determines if checkbox is checked or not
     */
    defaultChecked?: boolean;
    /**
     *  Display checkbox in error state
     */
    error?: boolean;
    /**
     *  Handler to listen for new value
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    /**
     *  Test id for unit and E2E tests
     */
    testId?: string;
    /**
     *  Test id for unit and E2E tests
     */
    Id?: string;
    /**
     * Determines if checkbox is in an indeterminate state
     */
    indeterminate?: boolean;
}

export const Checkbox: React.FC<CheckboxProps> = (props) => {
    const { checked, label, error, disabled, testId, Id, indeterminate, ...passThroughProps } = props;
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    React.useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = indeterminate || false;
        }
    }, [indeterminate]);

    return (
        <label htmlFor={Id} className={cx(styles.root)}>
            <input
                type="checkbox"
                checked={checked}
                {...passThroughProps}
                ref={inputRef}
                id={Id}
                className={cx(styles.fauxCheckbox)}
                disabled={disabled}
                aria-invalid={error ? true : null}
                data-testid={testId}
            />
            <span className={cx(styles['checkbox'], states, !indeterminate && !checked && styles['unchecked'])}>
                {indeterminate && <IconSubtract className={cx(styles.icon)} />}
                {checked && <IconCheck className={cx(styles.icon)} />}
            </span>

            <Typography variant="label-1" as="span" className={cx(styles.label)}>
                {label}
            </Typography>
        </label>
    );
};

Checkbox.displayName = 'Checkbox';
